import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { selectedQueueTickets, selectPrefetchSrParams, selectSearchSrParams } from 'features/queue/slice';
import { getQueryKeyFromParams, getRequestFromParams } from 'features/queue/grid/helpers';
import { postSearchSrRequest } from 'services/ticketService';
import { QUERY_STALE_TIME } from 'constant';
import { convertClientColumnConfig } from 'features/queue/utils';
import useIsGridPaginationEnabled from 'features/queue/newGrid/hooks/useIsGridPaginationEnabled';

const useQueueData = (options) => {
  const totalServiceRequest = useRef(null);
  const searchSrParams = useSelector(selectSearchSrParams);
  const prefetchSrParams = useSelector(selectPrefetchSrParams);
  const selectedTickets = useSelector(selectedQueueTickets);
  const isGridPaginationEnabled = useIsGridPaginationEnabled();

  const prefetchSearchSrParams = useMemo(
    () =>
      searchSrParams && prefetchSrParams?.startRow && prefetchSrParams?.endRow
        ? {
            ...searchSrParams,
            startRow: prefetchSrParams.startRow,
            endRow: prefetchSrParams.endRow,
          }
        : null,
    [searchSrParams, prefetchSrParams],
  );

  const searchSrRequest = getRequestFromParams(searchSrParams, selectedTickets);
  const prefetchSearchSrRequest = getRequestFromParams(prefetchSearchSrParams, selectedTickets);

  const queryFn = async () => {
    if (typeof global === 'undefined') {
      window.global = window;
    }
    global.queueAbortController = new AbortController();
    const response = await postSearchSrRequest(searchSrRequest, global.queueAbortController?.signal);
    return response;
  };

  const prefetchQueryFn = useCallback(async () => {
    if (typeof global === 'undefined') {
      window.global = window;
    }
    global.queueAbortController = new AbortController();
    const response = await postSearchSrRequest(prefetchSearchSrRequest, global.queueAbortController?.signal);
    return response;
  }, [prefetchSearchSrRequest]);

  const queryKey = useMemo(() => getQueryKeyFromParams(searchSrParams), [searchSrParams]);
  const prefetchQueryKey = useMemo(() => getQueryKeyFromParams(prefetchSearchSrParams), [prefetchSearchSrParams]);

  const queueData = useQuery(queryKey, {
    queryFn,
    enabled: !!searchSrParams && Object.keys(searchSrParams).length > 0 && !isGridPaginationEnabled,
    refetchOnWindowFocus: false,
    staleTime: QUERY_STALE_TIME.MINUTE,
    cacheTime: QUERY_STALE_TIME.MEDIUM,
    refetchOnMount: false,
    select: useCallback(
      (queueData) => ({
        ...queueData,
        columnConfiguration: queueData.columnConfiguration.map((col) => convertClientColumnConfig(col)),
      }),
      [],
    ),
    ...options,
  });

  const { data } = queueData;

  totalServiceRequest.current = totalServiceRequest.current ?? data?.countTotal;

  const queryClient = useQueryClient();

  useEffect(() => {
    if (
      prefetchSearchSrParams &&
      Object.keys(prefetchSearchSrParams).length > 0 &&
      (totalServiceRequest.current === null || prefetchSearchSrParams.startRow < totalServiceRequest.current)
    ) {
      queryClient.prefetchQuery({
        queryKey: prefetchQueryKey,
        queryFn: prefetchQueryFn,
        enabled: !isGridPaginationEnabled,
        staleTime: QUERY_STALE_TIME.SHORT,
        select: (queueData) => ({
          ...queueData,
          columnConfiguration: queueData.columnConfiguration.map((col) => convertClientColumnConfig(col)),
        }),
        ...options,
      });
    }
  }, [prefetchSearchSrParams, prefetchQueryKey, prefetchQueryFn, queryClient, options, isGridPaginationEnabled]);

  return queueData;
};

export default useQueueData;
